package com.schoolconnect.app.feature.schools

import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.border
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.Image
import androidx.compose.material3.Icon
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppPrimary
import io.github.vinceglb.filekit.compose.rememberFilePickerLauncher
import io.github.vinceglb.filekit.core.PickerType
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.building_school
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import com.schoolconnect.app.util.decodeImageBitmap
import com.schoolconnect.app.util.MAX_SCHOOL_LOGO_BYTES
import com.schoolconnect.app.util.resizeImage

/** Compact, shared picker used by both school create and update forms. */
@Composable
internal fun SchoolLogoPicker(
    selectedFileName: String,
    selectedBytes: ByteArray? = null,
    hasExistingLogo: Boolean = false,
    compact: Boolean = false,
    onSelected: (fileName: String, bytes: ByteArray) -> Unit,
    modifier: Modifier = Modifier
) {
    val scope = rememberCoroutineScope()
    val launcher = rememberFilePickerLauncher(
        type = PickerType.File(listOf("png", "jpg", "jpeg", "webp")),
        title = "Select School Logo"
    ) { file ->
        file ?: return@rememberFilePickerLauncher
        scope.launch {
            runCatching { file.readBytes() }
                .onSuccess { bytes ->
                    if (bytes.size <= MAX_SCHOOL_LOGO_BYTES) {
                        scope.launch {
                            val resizedBytes = resizeImage(bytes)
                            val resizedFileName = if (resizedBytes !== bytes) {
                                "${file.name.substringBeforeLast('.', file.name)}.jpg"
                            } else {
                                file.name
                            }
                            onSelected(resizedFileName, resizedBytes)
                        }
                    }
                }
        }
    }
    val selectedImage: ImageBitmap? = remember(selectedFileName, selectedBytes) {
        selectedBytes?.let(::decodeImageBitmap)
    }

    Row(
        modifier = modifier.widthIn(max = 560.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(14.dp)
    ) {
        Box(
            modifier = Modifier
                .size(if (compact) 76.dp else 64.dp)
                .border(2.dp, Color(0xFFBFD4FF), CircleShape)
                .background(Color(0xFFEAF2FF), CircleShape)
                .clickable(
                    indication = null,
                    interactionSource = remember { MutableInteractionSource() },
                    onClick = { launcher.launch() }
                ),
            contentAlignment = Alignment.Center
        ) {
            if (selectedImage != null) {
                Image(
                    bitmap = selectedImage,
                    contentDescription = "Selected school logo",
                    contentScale = ContentScale.Crop,
                    modifier = Modifier
                        .size(if (compact) 68.dp else 56.dp)
                        .clip(CircleShape)
                        .border(1.dp, Color.White, CircleShape)
                )
            } else {
                Icon(
                    painter = painterResource(Res.drawable.building_school),
                    contentDescription = "School logo",
                    tint = AppPrimary,
                    modifier = Modifier.size(if (compact) 36.dp else 32.dp)
                )
            }
        }
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(3.dp)) {
            Text("School logo", color = Color(0xFF172554))
            Text(
                text = when {
                    selectedFileName.isNotBlank() -> "Logo selected · click the circle to replace"
                    hasExistingLogo -> "Current logo saved · click the circle to replace"
                    else -> "Add a PNG, JPG or WEBP image"
                },
                color = AppMuted,
                maxLines = 1
            )
        }
    }
}
